package com.cs.ejobin.vm

import com.cs.ejobin.app.AppSession
import com.cs.ejobin.data.AuthRepository
// import com.cs.ejobin.data.SubscriptionRepository // ✅ Paiement désactivé (gardé pour plus tard)
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.model.Subscription
import com.cs.ejobin.utils.Validators
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

data class SignupState(
    val email: String = "",
    val phone: String = "",
    val password: String = "",
    val confirmPassword: String = "",
    val name: String = "",
    val selectedSkills: Set<SkillCatalog> = emptySet(),
    val isLoading: Boolean = false,
    val errorKey: String? = null,
    val acceptedLegal: Boolean = false,
    val description: String = ""
)

class SignupViewModel(
    private val auth: AuthRepository,
    // private val subs: SubscriptionRepository, // ✅ Paiement désactivé (gardé pour plus tard)
    private val session: AppSession
) : BaseViewModel() {

    private val _state = MutableStateFlow(SignupState())
    val state: StateFlow<SignupState> = _state.asStateFlow()

    fun onEmail(v: String) { _state.value = _state.value.copy(email = v, errorKey = null) }
    fun onPhone(v: String) { _state.value = _state.value.copy(phone = v, errorKey = null) }
    fun onPassword(v: String) { _state.value = _state.value.copy(password = v, errorKey = null) }
    fun onConfirmPassword(v: String) { _state.value = _state.value.copy(confirmPassword = v, errorKey = null) }
    fun onName(v: String) { _state.value = _state.value.copy(name = v, errorKey = null) }

    fun toggleSkill(skill: SkillCatalog) {
        val set = _state.value.selectedSkills.toMutableSet()
        if (!set.add(skill)) set.remove(skill)
        _state.value = _state.value.copy(selectedSkills = set, errorKey = null)
    }

    fun toggleAcceptedLegal() {
        _state.value = _state.value.copy(
            acceptedLegal = !_state.value.acceptedLegal,
            errorKey = null
        )
    }

    fun onDescription(v: String) {
        _state.value = _state.value.copy(description = v, errorKey = null)
    }

    fun signup(onSuccess: () -> Unit, onNeedEmailConfirmation: () -> Unit) {
        val s = _state.value

        if (s.email.trim().isEmpty()) {
            _state.value = s.copy(errorKey = Keys.required_email)
            return
        }
        if (!Validators.isValidPhone(s.phone)) {
            _state.value = s.copy(errorKey = Keys.invalid_phone)
            return
        }
        if (!Validators.isStrongEnoughPassword(s.password)) {
            _state.value = s.copy(errorKey = Keys.weak_password)
            return
        }
        if (s.password != s.confirmPassword) {
            _state.value = s.copy(errorKey = Keys.password_mismatch)
            return
        }
        if (s.name.trim().isEmpty()) {
            _state.value = s.copy(errorKey = Keys.required_name)
            return
        }
        if (s.selectedSkills.isEmpty()) {
            _state.value = s.copy(errorKey = Keys.select_one_skill)
            return
        }
        if (!s.acceptedLegal) {
            _state.value = s.copy(errorKey = Keys.accept_required)
            return
        }

        _state.value = s.copy(isLoading = true, errorKey = null)

        scope.launch {
            runCatching {
                auth.signup(
                    email = s.email.trim(),
                    password = s.password,
                    name = s.name.trim(),
                    phone = s.phone.trim(),
                    skills = s.selectedSkills.map { it.label },
                    description = s.description.trim()
                )
            }.onSuccess { result ->
                when (result) {
                    is com.cs.ejobin.data.SignupResult.SignedIn -> {
                        // ✅ Paiement désactivé : on force un état "connecté" sans abonnement
                        session.setLoggedIn(result.profile, Subscription.None)

                        // 🔁 À remettre plus tard :
                        // val subscription = subs.getCurrentSubscription(result.profile.id)
                        // session.setLoggedIn(result.profile, subscription)

                        _state.value = _state.value.copy(isLoading = false)
                        onSuccess()
                    }

                    com.cs.ejobin.data.SignupResult.NeedsEmailConfirmation -> {
                        _state.value = _state.value.copy(isLoading = false)
                        onNeedEmailConfirmation()
                    }
                }
            }.onFailure {
                _state.value = _state.value.copy(isLoading = false, errorKey = Keys.error_generic)
            }
        }
    }
}